import { useCallback, useMemo } from "react";
import ScrollArea from "../ScrollArea.jsx";
import { renderInline } from "../Markdown.jsx";
import HoverTooltip from "../HoverTooltip.jsx";
import MemoryStoreMap from "./MemoryStoreMap.jsx";
import { useMemoryStore } from "./useMemoryStore.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { labelForMemory } from "../../lib/memoryNames.js";
import { dayLabelFormatter, parseServerTimestamp } from "../../lib/traceUtils.js";
import { buildMemoryGraph, parseIndexEntries } from "../../lib/memoryGraph.js";
import {
  extractMemoryRefs,
  memorySlugFromHref,
  slugFromWikiTarget,
  splitFrontmatter,
} from "../../lib/memoryLinks.js";

// Renders one memory file's body with its cross-references made TRAVERSABLE, and
// — for the index (MEMORY.md) — a map of the whole store.
//
// The harness memory store is a graph the dashboard has only ever shown as flat
// text: the index points at each fact with `[Title](slug.md)`, leaf facts
// cross-link each other with `[[slug]]`. This component:
//   • resolves every inline pointer against the captured set (the set the
//     sidebar lists): LIVE (captured → click navigates), GHOST (named by the
//     index but never captured → muted dashed marker with a hover explanation —
//     the case that made the index look broken), or SELF;
//   • for the index, draws a radial store MAP (MemoryStoreMap) above the text;
//   • for a leaf, shows BACKLINKS ("referenced by …") so edges are walkable both
//     ways.
// It owns its data fetch (useMemoryStore) so it stays self-contained when stored
// as ScopedView's lensDetail.content.
export default function MemoryBody({ body, slug, ancestorScope = [], range, onNavigate }) {
  const isIndex = slug === "MEMORY";
  const { frontmatter, body: prose } = useMemo(() => splitFrontmatter(body || ""), [body]);

  const store = useMemoryStore({ ancestorScope, range, deep: isIndex, trackSlug: isIndex ? null : slug });
  const { capturedSlugs, capturedLoading, bodies, indexBody, indexVersions, deepLoading } = store;

  // Index text source: the version we're viewing for the index itself; the
  // hydrated index for a leaf (so its backlinks know what the index points at).
  const indexSource = isIndex ? body : indexBody;
  const graph = useMemo(
    () => buildMemoryGraph({
      indexEntries: parseIndexEntries(indexSource || ""),
      capturedSlugs,
      bodies,
      current: slug,
    }),
    [indexSource, capturedSlugs, bodies, slug],
  );

  // Coverage line is for LEAF bodies — the index gets the map header instead.
  const coverage = useMemo(() => {
    if (isIndex) return null;
    const refs = extractMemoryRefs(prose).filter((r) => r !== slug);
    if (!refs.length) return null;
    let captured = 0;
    for (const r of refs) if (capturedSlugs.has(r)) captured += 1;
    return { total: refs.length, captured, ghost: refs.length - captured };
  }, [isIndex, prose, capturedSlugs, slug]);

  // Captured memories this index SNAPSHOT doesn't point at — explains why the
  // store has more captured files than this version references.
  const versionOrphans = useMemo(() => {
    if (!isIndex || capturedLoading) return 0;
    const inMap = new Set(graph.nodes.map((n) => n.slug));
    let k = 0;
    for (const s of capturedSlugs) if (s !== "MEMORY" && !inMap.has(s)) k += 1;
    return k;
  }, [isIndex, capturedLoading, graph, capturedSlugs]);

  const backlinks = isIndex ? [] : (graph.backlinks.get(slug) || []);
  const isOrphan = !isIndex && capturedSlugs.has(slug) && !backlinks.length && !deepLoading;

  // For an orphan, the most-recent index version that still listed it — turns a
  // dead "not referenced" into "dropped from the index after <date>".
  const lastListed = useMemo(() => {
    if (isIndex || backlinks.length) return null;
    const v = (indexVersions || []).find((x) => x.slugs.includes(slug));
    if (!v) return null;
    const ms = parseServerTimestamp(v.ts);
    return Number.isFinite(ms) ? dayLabelFormatter.format(ms) : null;
  }, [isIndex, backlinks.length, indexVersions, slug]);

  const renderLink = useCallback(({ href, target, wiki, children, key }) => {
    const targetSlug = wiki ? slugFromWikiTarget(target) : memorySlugFromHref(href);
    if (!targetSlug) return undefined; // not a memory pointer → default <a> / literal wikilink
    const display = children ?? labelForMemory(targetSlug);

    if (targetSlug === slug) {
      return <span key={key} className="font-medium text-[color:var(--ink)]">{display}</span>;
    }
    if (capturedSlugs.has(targetSlug)) {
      return (
        <button
          key={key}
          type="button"
          onClick={() => onNavigate?.(targetSlug)}
          className="cursor-pointer bg-transparent p-0 text-left underline decoration-[color:var(--green)] underline-offset-2 text-[color:var(--ink)] transition-colors hover:decoration-[color:var(--ink)]"
        >
          {display}
        </button>
      );
    }
    if (capturedLoading) {
      return (
        <span key={key} className="underline decoration-dotted decoration-[color:var(--line-strong)] underline-offset-2 text-[color:var(--muted)]">
          {display}
        </span>
      );
    }
    return (
      <HoverTooltip
        key={key}
        placement="top"
        wide
        label={`No captured content for ${targetSlug}.md in this scope and time range. The index references it, but no scanned prompt accessed the file. Widen the date range to look for earlier activity.`}
      >
        <span tabIndex={0} className="cursor-help underline decoration-dashed decoration-[color:var(--line-strong)] underline-offset-2 text-[color:var(--muted)]">
          {display}
        </span>
      </HoverTooltip>
    );
  }, [capturedSlugs, capturedLoading, slug, onNavigate]);

  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="px-[2px] py-[8px]">
      {isIndex ? (
        <div className="mb-[10px] border-b border-[color:var(--line-soft)] pb-[10px]">
          <MemoryStoreMap graph={graph} current={slug} onNavigate={onNavigate} loading={capturedLoading} linking={deepLoading} />
          {versionOrphans ? (
            <div className={`${NOTE_CLASS} mt-[6px]`}>
              + {versionOrphans} captured {versionOrphans === 1 ? "memory" : "memories"} not in this snapshot of the index
            </div>
          ) : null}
        </div>
      ) : null}

      {!isIndex && (backlinks.length || isOrphan) ? (
        <div className={`${NOTE_CLASS} mb-[8px] flex flex-wrap items-center gap-x-[6px] gap-y-[2px]`}>
          {backlinks.length ? (
            <>
              <span>Referenced by</span>
              {backlinks.map((b, i) => (
                <button
                  key={`${b.from}-${i}`}
                  type="button"
                  onClick={() => onNavigate?.(b.from)}
                  className="cursor-pointer bg-transparent p-0 underline decoration-[color:var(--green)] underline-offset-2 text-[color:var(--ink)] hover:decoration-[color:var(--ink)]"
                >
                  {b.from === "MEMORY" ? "Memory index" : labelForMemory(b.from)}
                </button>
              ))}
            </>
          ) : (
            <span>
              {lastListed
                ? `Dropped from the index. Last listed ${lastListed}.`
                : "Not referenced by the index. Orphan capture."}
            </span>
          )}
        </div>
      ) : null}

      {coverage ? (
        <div className={`${NOTE_CLASS} mb-[8px]`}>
          References {coverage.total} {coverage.total === 1 ? "memory" : "memories"} · {coverage.captured} captured
          {coverage.ghost ? ` · ${coverage.ghost} uncaptured` : ""}
        </div>
      ) : null}

      {frontmatter ? (
        <pre className="mb-[8px] overflow-x-auto whitespace-pre-wrap break-words border-l-[2px] border-[color:var(--line-strong)] pl-[8px] font-mono text-[11px] leading-[1.5] text-[color:var(--muted)]">
          {frontmatter}
        </pre>
      ) : null}

      {/* Line-break-faithful: the source's own newlines/indentation survive
          (whitespace-pre-wrap), with cross-links + inline emphasis resolved —
          NOT the block parser, which would collapse the file into paragraphs. */}
      <div className="whitespace-pre-wrap break-words font-mono text-[12px] leading-[1.55] text-[color:var(--ink)]">
        {renderInline(prose, { renderLink, wikiLinks: true })}
      </div>
    </ScrollArea>
  );
}
